import React, {useState} from 'react'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import {useLocation} from 'react-router-dom'
import H from 'history'
import {MBMemberError} from '../../../Common/MBMemberError'
import {MemberEditAddressInfo} from '../../../Props/MembersEditAddressPageLocationStates'
import MemberRepo from '../../../Repo/MemberRepo'
import {MemberAddressDTO} from '../../../DTO/MemberAddressDTO'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {animateScroll} from 'react-scroll'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {ContentBlock} from '../../Molecules/BaseContentBlocks/ContentBlock'
import {ContentTitle} from '../../Molecules/BaseContentBlocks/ContentTitle'
import {ContentTopMessage} from '../../Molecules/BaseContentBlocks/ContentTopMessage'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {ErrorDisplay} from '../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {MBContentBodyDivider} from '../../Atoms/Dividers'
import {ContactAddressConfirmContents} from '../../Components/ConfirmContentBlocks/AddressConfirms/ContactAddressConfirmContents'

type Props = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

export function MembersEditAddressConfirmPage(props: Props): JSX.Element {
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [processing, setProcessing] = useState<boolean>(false)

  const location: H.Location<MemberEditAddressInfo> = useLocation<MemberEditAddressInfo>()
  const locationState: MemberEditAddressInfo = location.state

  const throwMBMemberError = useMBMemberError()

  function isInvalidLocationState(locationState: MemberEditAddressInfo): boolean {
    if (!locationState) {
      return true
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {phoneMobile, phoneTel, addressBuilding, ...rest} = locationState
    if (phoneMobile === undefined && phoneTel === undefined) {
      return true
    }
    return Object.values(rest).some(value => value === undefined)
  }

  if (isInvalidLocationState(locationState)) {
    throw new MBMemberError('InvalidOperation')
  }

  const goToEditAddressCompletePage = async (): Promise<void> => {
    setProcessing(true)
    try {
      const response = await props.memberRepo.putAddress(new MemberAddressDTO(
        locationState.postCode,
        locationState.addressPrefectureCode,
        locationState.addressCity,
        locationState.addressStreet,
        locationState.addressBuilding,
        locationState.phoneMobile,
        locationState.phoneTel,
        ),
        true,
      )

      switch (response.status) {
        case CommonResponseStatus.Ok:
          props.navigator.goToEditAddressCompletePage()
          break
        case CommonResponseStatus.BadRequest:
          setProcessing(false)
          setErrorMessages(response.errorMessageList)
          animateScroll.scrollToTop()
          break
        default:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
      }

    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'SystemError'
      throwMBMemberError(errorMessage)
    }
  }

  const {
    postCode,
    addressPrefectureCode,
    addressCity,
    addressStreet,
    addressBuilding,
    phoneMobile,
    phoneTel,
  } = locationState

  return (
    <MembersPageTemplate>
      <PageTitle>連絡先情報変更確認</PageTitle>

      <StepGuide activity={'edit'} current={'confirm'}/>

      <ContentBlock data-testid={'block-confirms'}>
        <ContentTitle>連絡先情報</ContentTitle>
        <PIMStack spacing={'18px'}>
          <ContentTopMessage>
            変更はまだ完了していません。<br/>
            下記内容をご確認の上、「確定する」ボタンを押し変更を完了してください。
          </ContentTopMessage>
          <MBContentBodyDivider/>
          <ContactAddressConfirmContents
            postCode={postCode}
            addressPrefectureCode={addressPrefectureCode}
            addressCity={addressCity}
            addressStreet={addressStreet}
            addressBuilding={addressBuilding}
            phoneMobile={phoneMobile}
            phoneTel={phoneTel}
          />
        </PIMStack>
      </ContentBlock>

      <MessageForAction>
        上記の内容に間違いがなければ<br/>『確定する』ボタンを押してください。
      </MessageForAction>

      <PIMStack alignItems={'center'}>
        <ErrorDisplay errors={errorMessages}/>
      </PIMStack>

      <PIMPageActions>
        <PIMSubmitButton
          testId={'determine-button'}
          disabled={processing}
          onClick={(): Promise<void> => goToEditAddressCompletePage()}
        >
          確定する
        </PIMSubmitButton>
        <PIMSecondaryButton onClick={(): void => props.navigator.goBack()}>
          連絡先情報変更に戻る
        </PIMSecondaryButton>
      </PIMPageActions>
    </MembersPageTemplate>
  )
}
